import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Delete as DeleteIcon, Plus, Drag as DragIcon } from '@icon-park/react';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, useSortable, arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import './styles/ProductBasicInfo.css';

function SortableField({ id, children }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.9 : 1,
    boxShadow: isDragging ? '0 8px 20px rgba(0,0,0,0.12)' : undefined
  };

  return (
    <div ref={setNodeRef} style={style} className="form-row custom-field sortable-field">
      {children({ attributes, listeners })}
    </div>
  );
}

export default function ProductCoreData({ data, onUpdate }) {
  const [expanded, setExpanded] = useState(true);
  const fields = useMemo(() => ([
    { key: 'giValue', label: 'GI 值', placeholder: '请输入 GI 值' },
    { key: 'glValue', label: 'GL 值', placeholder: '请输入 GL 值' }
  ]), []);
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 5 }
    })
  );
  const syncTimer = useRef(null);

  useEffect(() => () => {
    if (syncTimer.current) {
      clearTimeout(syncTimer.current);
    }
  }, []);

  useEffect(() => {
    const custom = data.customFields || [];
    const timestamp = Date.now();
    let changed = false;
    const updatedCustom = custom.map((field, index) => {
      if (!field._id) {
        changed = true;
        return { ...field, _id: `pcd-${timestamp}-${index}` };
      }
      return field;
    });

    const baseIds = fields.map((field) => field.key);
    const customIds = updatedCustom.map((field) => field._id);
    const defaultOrder = [...baseIds, ...customIds];
    const existingOrder = data.fieldOrder || [];
    const mergedOrder = [
      ...existingOrder.filter((id) => defaultOrder.includes(id)),
      ...defaultOrder.filter((id) => !existingOrder.includes(id))
    ];

    const orderChanged = mergedOrder.length !== existingOrder.length ||
      mergedOrder.some((id, index) => id !== existingOrder[index]);

    if (changed || orderChanged) {
      onUpdate({ ...data, customFields: updatedCustom, fieldOrder: mergedOrder });
    }
  }, [data, fields, onUpdate]);

  const allItems = useMemo(() => {
    const baseItems = fields.map((field) => ({
      ...field,
      _id: field.key,
      type: 'base',
      value: data[field.key] || ''
    }));
    const customItems = (data.customFields || []).map((field) => ({
      ...field,
      _id: field._id,
      type: 'custom'
    }));
    const order = data.fieldOrder || [...baseItems.map((item) => item._id), ...customItems.map((item) => item._id)];
    const map = [...baseItems, ...customItems].reduce((acc, item) => {
      acc[item._id] = item;
      return acc;
    }, {});
    return order.map((id) => map[id]).filter(Boolean);
  }, [data, fields]);

  const [localItems, setLocalItems] = useState(allItems);

  useEffect(() => {
    const sameLength = localItems.length === allItems.length;
    const sameIds = sameLength && localItems.every((item, index) => item?._id === allItems[index]?._id);
    if (!sameIds) {
      setLocalItems(allItems);
    }
  }, [allItems, localItems]);

  const scheduleSync = (nextItems) => {
    if (syncTimer.current) {
      clearTimeout(syncTimer.current);
    }
    syncTimer.current = setTimeout(() => {
      const updatedData = { ...data };
      nextItems.forEach((item) => {
        if (item.type === 'base') {
          updatedData[item.key] = item.value || '';
        }
      });
      updatedData.customFields = nextItems
        .filter((item) => item.type === 'custom')
        .map(({ type, ...rest }) => rest);
      updatedData.fieldOrder = nextItems.map((item) => item._id);
      onUpdate(updatedData);
    }, 120);
  };

  const handleBaseFieldChange = (key, value) => {
    const next = localItems.map((item) => item._id === key ? { ...item, value } : item);
    setLocalItems(next);
    scheduleSync(next);
  };

  const handleCustomFieldChange = (id, field, value) => {
    const next = localItems.map((item) => {
      if (item._id !== id) {
        return item;
      }
      return field === 'label'
        ? { ...item, label: value }
        : { ...item, value };
    });
    setLocalItems(next);
    scheduleSync(next);
  };

  const handleAddField = () => {
    const newItem = {
      label: '新字段',
      value: '',
      _id: `pcd-${Date.now()}-${(data.customFields || []).length}`
    };
    const next = [...localItems, { ...newItem, type: 'custom' }];
    setLocalItems(next);
    scheduleSync(next);
  };

  const handleDeleteCustomField = (id) => {
    const next = localItems.filter((item) => item._id !== id);
    setLocalItems(next);
    scheduleSync(next);
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) {
      return;
    }
    const oldIndex = localItems.findIndex((item) => item._id === active.id);
    const newIndex = localItems.findIndex((item) => item._id === over.id);
    if (oldIndex === -1 || newIndex === -1) {
      return;
    }
    const reordered = arrayMove(localItems, oldIndex, newIndex);
    setLocalItems(reordered);
    scheduleSync(reordered);
  };

  return (
    <div className="product-basic-info-container">
      <div className="template-header">
        <div className="template-header-main">
          <h3 className="template-title">输入产品核心数据</h3>
          <button className="template-toggle" onClick={() => setExpanded((prev) => !prev)} type="button">
            {expanded ? '折叠' : '展开'}
          </button>
        </div>
      </div>

      {expanded && (
        <div className="info-form">
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={localItems.map((item) => item._id)}>
              {localItems.map((field) => (
                <SortableField key={field._id} id={field._id}>
                  {({ attributes, listeners }) => (
                    <>
                      {field.type === 'base' ? (
                        <label className="form-label">{field.label}</label>
                      ) : (
                        <input
                          type="text"
                          className="form-label-input"
                          placeholder="字段名称"
                          value={field.label}
                          onChange={(e) => handleCustomFieldChange(field._id, 'label', e.target.value)}
                        />
                      )}
                      <textarea
                        className="form-textarea"
                        placeholder={field.placeholder || '字段内容'}
                        value={field.value ?? ''}
                        onChange={(e) => {
                          if (field.type === 'base') {
                            handleBaseFieldChange(field._id, e.target.value);
                          } else {
                            handleCustomFieldChange(field._id, 'value', e.target.value);
                          }
                        }}
                        rows={2}
                      />
                      <div className="custom-field-actions">
                        <button
                          className="drag-handle"
                          {...attributes}
                          {...listeners}
                          title="拖拽排序"
                          type="button"
                        >
                          <DragIcon theme="outline" size={16} fill="#111827" strokeWidth={3} />
                        </button>
                        {field.type === 'custom' ? (
                          <button
                            className="delete-field-btn"
                            onClick={() => handleDeleteCustomField(field._id)}
                            type="button"
                          >
                            <DeleteIcon theme="outline" size={14} fill="#333" strokeWidth={3} />
                          </button>
                        ) : (
                          <button
                            className="delete-field-btn disabled-delete"
                            title="默认字段不可删除"
                            type="button"
                            disabled
                          >
                            <DeleteIcon theme="outline" size={14} fill="#999" strokeWidth={3} />
                          </button>
                        )}
                      </div>
                    </>
                  )}
                </SortableField>
              ))}
            </SortableContext>
          </DndContext>

          <button className="add-field-btn" onClick={handleAddField} type="button">
            <span className="add-icon"><Plus theme="outline" size={18} fill="#333" strokeWidth={3} /></span>
            添加自定义字段
          </button>
        </div>
      )}
    </div>
  );
}
